// components/ListaClienteDetalhe.tsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  MoneyOff as MoneyOffIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Cliente, MENSAGENS } from '@types';
import { SessionUtil } from '@utils';
import clienteController from '@controllers/clienteController';

const ListaClienteDetalhe: React.FC = () => {
  const { codigo } = useParams<{ codigo: string }>();
  const navigate = useNavigate();

  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const vendedor = SessionUtil.getVendedor();
  const vendCodigo = vendedor?.vendCodigo;

  useEffect(() => {
    carregarCliente();
  }, [codigo]);

  const carregarCliente = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('ListaClienteDetalhe - Iniciando carregamento');
      console.log('ListaClienteDetalhe - codigo:', codigo);
      console.log('ListaClienteDetalhe - vendCodigo:', vendCodigo);

      if (!codigo || !vendCodigo) {
        console.log('ListaClienteDetalhe - Dados insuficientes');
        setError('Dados insuficientes para carregar cliente');
        return;
      }

      const data = await clienteController.obterClienteDados(codigo, vendCodigo);
      console.log('ListaClienteDetalhe - Dados recebidos:', data);
      console.log('ListaClienteDetalhe - Tipo de data:', typeof data);
      console.log('ListaClienteDetalhe - É objeto?', data && typeof data === 'object');
      console.log('ListaClienteDetalhe - Tem cpfCnpj?', data?.cpfCnpj);

      setCliente(data);
      console.log('ListaClienteDetalhe - Cliente setado no state');
    } catch (err) {
      console.error('ListaClienteDetalhe - Erro capturado:', err);
      setError('Erro ao carregar dados do cliente. Tente novamente.');
    } finally {
      setLoading(false);
      console.log('ListaClienteDetalhe - Loading finalizado');
    }
  };

  const handleVoltar = () => {
    navigate('/clientes');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !cliente) {
    console.log('ListaClienteDetalhe - Condição de erro ativada');
    console.log('ListaClienteDetalhe - error:', error);
    console.log('ListaClienteDetalhe - cliente:', cliente);
    console.log('ListaClienteDetalhe - !cliente:', !cliente);
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error || 'Cliente não encontrado'}</Alert>
      </Box>
    );
  }

  const ativo = clienteController.clienteAtivo(cliente);
  const status = clienteController.obterStatusCliente(cliente);

  return (
    <Box sx={{ pb: 10 }}>
      {/* Header com botão voltar */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={handleVoltar}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">Detalhes do Cliente</Typography>
      </Box>

      {/* Avatar e Nome */}
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            margin: '0 auto',
            bgcolor: ativo ? 'primary.main' : 'error.main'
          }}
        >
          <PersonIcon sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h5" sx={{ mt: 2, fontWeight: 'bold' }}>
          {cliente.nome}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Código: {cliente.codigo}
        </Typography>
      </Box>

      {/* Alerta de Status */}
      {!ativo && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity="error" icon={<MoneyOffIcon />}>
            <Typography fontWeight="bold">
              {MENSAGENS.CLIENTE_SEM_CREDITO}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Este cliente está com restrições financeiras e não pode realizar novos pedidos.
              Entre em contato com o setor financeiro para regularização.
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Informações do Cliente */}
      <Box sx={{ px: 2 }}>
        {/* Dados Principais */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              DADOS PRINCIPAIS
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" gap={1} alignItems="flex-start">
                  <BusinessIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Razão Social
                    </Typography>
                    <Typography variant="body1">
                      {cliente.razao || 'Não informado'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {cliente.cpfCnpj && (
                <Grid item xs={12}>
                  <Box display="flex" gap={1} alignItems="flex-start">
                    <BusinessIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        CPF/CNPJ
                      </Typography>
                      <Typography variant="body1">
                        {clienteController.formatarCNPJ(cliente.cpfCnpj)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}

              {cliente.email && (
                <Grid item xs={12}>
                  <Box display="flex" gap={1} alignItems="flex-start">
                    <EmailIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        E-mail
                      </Typography>
                      <Typography variant="body1">{cliente.email}</Typography>
                    </Box>
                  </Box>
                </Grid>
              )}

              {cliente.fone && (
                <Grid item xs={12}>
                  <Box display="flex" gap={1} alignItems="flex-start">
                    <PhoneIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Telefone
                      </Typography>
                      <Typography variant="body1">{cliente.fone}</Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Endereço */}
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              ENDEREÇO
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box display="flex" gap={1} alignItems="flex-start">
              <LocationIcon color="action" />
              <Box>
                <Typography variant="body1">
                  {cliente.endereco}
                  {cliente.numero && `, ${cliente.numero}`}
                </Typography>
                {cliente.complemento && (
                  <Typography variant="body2" color="text.secondary">
                    {cliente.complemento}
                  </Typography>
                )}
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {cliente.bairro}
                </Typography>
                <Typography variant="body1">
                  {cliente.cidade}
                  {cliente.estado && ` - ${cliente.estado}`}
                </Typography>
                {cliente.cep && (
                  <Typography variant="body2" color="text.secondary">
                    CEP: {cliente.cep}
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Informações Adicionais */}
        <Card sx={{ mt: 2, mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              INFORMAÇÕES ADICIONAIS
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Tipo
                </Typography>
                <Typography variant="body1">
                  {cliente.b2b === '1' ? 'B2B' : 'B2C'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Typography
                  variant="body1"
                  color={ativo ? 'success.main' : 'error.main'}
                  fontWeight="bold"
                >
                  {status.mensagem}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ListaClienteDetalhe;
